import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '@/components/functional/DataTable/DataTable';
import { createColumnHelper } from '@tanstack/react-table';
import { ChildUser } from '@/api/useChildUsers';
import supabaseClient from '@/api/supabaseClient';
import ConfirmDeleteModal from '@/components/modals/ConfirmDeleteModal/ConfirmDeleteModal';

type ChildUserWithOrg = ChildUser & { organizationName: string };
const columnHelper = createColumnHelper<ChildUserWithOrg>();

const RequestsPage: FC = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [allChildUsers, setAllChildUsers] = useState<ChildUserWithOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter only pending requests
  const pendingRequests = allChildUsers.filter(user => user.status === 'Pending');

  // Modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    childUserId: string | null;
    childUserName: string;
  }>({
    isOpen: false,
    childUserId: null,
    childUserName: '',
  });

  useEffect(() => {
    const fetchAllChildUsers = async () => {
      try {
        // Fetch ALL child users directly
        const { data: allChildUsersData } = await supabaseClient
          .from('users')
          .select('id, name, auth_id, created_by')
          .eq('user_type', 'child_user');

        // Get parent organization names and status for all children
        const allChildren: ChildUserWithOrg[] = await Promise.all(
          (allChildUsersData || []).map(async (child) => {
            let parentOrgName = 'N/A';
            
            // Fetch parent organization by created_by
            if (child.created_by) {
              const { data: parentUser } = await supabaseClient
                .from('users')
                .select('name, organization_name')
                .eq('id', child.created_by)
                .single();
              
              if (parentUser) {
                parentOrgName = parentUser.organization_name || parentUser.name || 'N/A';
              }
            }
            
            // Check if user has verified their email
            const { data: authData } = await supabaseClient.auth.admin.getUserById(child.auth_id);
            const status = authData?.user?.email_confirmed_at ? 'Active' : 'Pending';

            return {
              id: child.id,
              name: child.name || 'N/A',
              status,
              createdAt: '',
              organizationName: parentOrgName
            };
          })
        );

        setAllChildUsers(allChildren);
      } catch (error) {
        console.error('Error fetching child users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllChildUsers();
  }, []);

  const openDeleteModal = (childUserId: string, childUserName: string) => {
    setDeleteModal({
      isOpen: true,
      childUserId,
      childUserName,
    });
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setDeleteModal({
        isOpen: false,
        childUserId: null,
        childUserName: '',
      });
    }
  };

  const handleRemove = async () => {
    if (!deleteModal.childUserId) return;
    
    try {
      setIsDeleting(true);
      const childUserId = deleteModal.childUserId;
      
      // Get child user data
      const { data: childUserData } = await supabaseClient
        .from('users')
        .select('auth_id')
        .eq('id', childUserId)
        .single();

      if (!childUserData?.auth_id) {
        console.error('Child user not found');
        return;
      }

      // Get email from auth
      const { data: authData } = await supabaseClient.auth.admin.getUserById(childUserData.auth_id);
      const email = authData?.user?.email;

      if (email) {
        // Delete from assessment_users table by email (this will cascade delete user_assessments and user_responses)
        await supabaseClient
          .from('assessment_users')
          .delete()
          .eq('email', email);
      }

      // Delete from users table
      const { error: deleteUserError } = await supabaseClient
        .from('users')
        .delete()
        .eq('id', childUserId);

      if (deleteUserError) throw deleteUserError;

      // Delete from auth
      await supabaseClient.auth.admin.deleteUser(childUserData.auth_id);

      // Refresh the child users list
      setAllChildUsers(prev => prev.filter(child => child.id !== childUserId));
      closeDeleteModal();
    } catch (error) {
      console.error('Error removing child user:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    columnHelper.accessor('name', {
      header: () => t('Child User'),
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('organizationName', {
      header: () => t('Organization Name'),
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('id', {
      header: () => t('Action'),
      cell: info => (
        <button
          onClick={() => openDeleteModal(info.getValue(), info.row.original.name)}
          className="px-3 py-1 text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors"
        >
          {t('Remove')}
        </button>
      ),
    }),
  ];

  // Filter pending requests by search query
  const filteredRequests = pendingRequests.filter(request => {
    const searchLower = searchQuery.toLowerCase();
    return (
      request.name.toLowerCase().includes(searchLower) ||
      (request.organizationName || '').toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="flex flex-col gap-4">
      <div className='bg-white rounded-2xl px-6 py-4 shadow-assessment-sm flex justify-between items-center'>
        <h1 className='text-4xl font-bold'>{t('Requests')}</h1>
        <div className="flex items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Search...')}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <p>{t('Loading...')}</p>
        </div>
      ) : filteredRequests.length === 0 ? (
        <div className="bg-white rounded-2xl px-6 py-12 shadow-assessment-sm text-center">
          <p className="text-gray-500 text-lg">
            {pendingRequests.length === 0 
              ? t('No pending requests at the moment')
              : t('No requests match your search')}
          </p>
        </div>
      ) : (
        <DataTable 
          data={filteredRequests} 
          columns={columns} 
          className="[&_tbody_tr:nth-child(odd)]:bg-table-stripe" 
        />
      )}

      {/* Delete Request Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleRemove}
        title={t('Confirm Delete Child User')}
        message={t('Are you sure you want to delete user "{{name}}"? This action cannot be undone and will remove all their assessment data.', { name: deleteModal.childUserName })}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default RequestsPage;
